import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { SITE_COPY, type PublicProfileResponse, type TimelineResponse } from '@lifetimeline/shared';

import { PickDetail, type PickDetailData } from '../components/PickDetail.js';
import { TimelineWithFilter } from '../components/TimelineWithFilter.js';
import { Avatar } from '../components/Avatar.js';
import { EmptyState } from '../components/EmptyState.js';
import { LockIcon } from '../components/icons.js';
import { ConnectButton } from '../features/connection/ConnectButton.js';
import { ProfileTabs, useProfileTab } from '../components/ProfileTabs.js';
import { PickGrid, PickGridCell } from '../components/PickGrid.js';
import { Button } from '../components/Button.js';
import { Layout } from '../components/Layout.js';
import { Notice } from '../components/Notice.js';
import { Spinner } from '../components/Spinner.js';
import { useToast } from '../components/Toast.js';
import { useI18n } from '../i18n/index.js';
import { ApiError, api, errorMessageKey } from '../lib/apiClient.js';
import { profileDocumentTitle, profileHandle } from '../lib/branding.js';
import { categoryName, loadCatalog, type LoadedCatalog } from '../lib/catalog.js';
import styles from './pageStyles.module.css';

/**
 * 공개 프로필 — `life-timeline.com/@junho`.
 *
 * 로그인 없이 누구나 본다. 공유가 이 서비스의 목적이라 가장 중요한 화면이다.
 *
 * ⚠️ **카테고리 이름은 서버가 주지 않는다.** 보는 사람의 언어로 표시해야 하므로
 * `categoryId`만 받아 여기서 자기 언어 카탈로그로 해석한다(계획 §4).
 * 한국인의 프로필을 영어 사용자가 보면 카테고리는 영어로, 아이템 이름은
 * 주인이 쓴 그대로 보인다.
 */
export function PublicProfilePage() {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const { handle } = useParams<{ handle: string }>();
  const toast = useToast();
  const [tab, setTab] = useProfileTab();
  const [more, setMore] = useState(false);

  /**
   * `@`는 URL 표기일 뿐 식별자가 아니다. 저장·전송되는 값은 `junho`다.
   * 여기서 떼어내 아래로는 순수한 userId만 흐르게 한다.
   */
  const userId = handle !== undefined && handle.startsWith('@') ? handle.slice(1) : null;

  const [data, setData] = useState<PublicProfileResponse | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<LoadedCatalog | null>(null);
  /* 격자에는 한 줄 설명이 안 보인다 — 누르면 크게 펼친다 */
  const [detail, setDetail] = useState<PickDetailData | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadCatalog(language)
      .then((loaded) => {
        if (!cancelled) {
          setCatalog(loaded);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [language]);

  const [reloadToken, setReloadToken] = useState(0);
  const reload = () => setReloadToken((n) => n + 1);

  /**
   * 지금 화면에 떠 있는 데이터가 **누구 것인지**.
   *
   * 다시 읽을 때 화면을 비울지 정하는 데 쓴다 — 아래 참조.
   */
  const shownFor = useRef<string | null>(null);

  useEffect(() => {
    if (userId === null) {
      return;
    }
    let cancelled = false;

    /*
     * ⚠️ **사람이 바뀔 때만 비운다.**
     *
     * 전에는 다시 읽을 때마다 `setData(null)`을 했다. 그러면 인연 요청을
     * 누른 직후(관계가 바뀌어 다시 읽는다) 화면이 통째로 사라졌다가 다시
     * 그려져서, 페이지를 새로고침한 것처럼 깜빡였다.
     *
     * 다른 사람으로 넘어갈 때는 비우는 게 맞다 — 안 비우면 새 프로필이
     * 올 때까지 **앞사람의 기록이 새 주소 아래 떠 있다.**
     */
    if (shownFor.current !== userId) {
      setData(null);
      shownFor.current = null;
    }
    setNotFound(false);
    setError(null);

    /*
     * **익명으로 부르지 않는다.** 남의 프로필은 로그인해야 열린다 —
     * 토큰을 안 붙이면 서버가 401을 준다.
     */
    api
      .get<PublicProfileResponse>(`/api/profiles/${encodeURIComponent(userId)}`)
      .then((loaded) => {
        if (!cancelled) {
          shownFor.current = userId;
          setData(loaded);
        }
      })
      .catch((cause: unknown) => {
        if (cancelled) {
          return;
        }
        if (cause instanceof ApiError && cause.status === 404) {
          shownFor.current = null;
          setData(null);
          setNotFound(true);
          return;
        }
        setError(t(errorMessageKey(cause)));
      });

    return () => {
      cancelled = true;
    };
  }, [userId, t, reloadToken]);

  // 프로필 주인의 이름을 문서 제목에 넣는다. 링크 미리보기·검색엔진에 나가는
  // 값은 API가 HTML에 박아 내려준다(`profilePageService`) — 같은 함수를 쓴다.
  //
  // `language`를 지켜보는 이유: 언어를 바꾸면 I18nProvider가 탭 제목을 서비스
  // 소개로 되돌리는데, 그 뒤에 사람 이름을 다시 얹어야 한다.
  useEffect(() => {
    if (data !== null) {
      document.title = profileDocumentTitle(data.profile.userName, data.profile.handle);
    }
    return () => {
      document.title = SITE_COPY[language].title;
    };
  }, [data, language]);

  /**
   * 남의 타임라인 다음 쪽.
   *
   * 권한은 **서버가 다시 본다** — 커서만 만들어서 잠긴 타임라인을 읽어 가는
   * 길을 막으려는 것이다.
   */
  const loadMore = async () => {
    const cursor = data?.timeline?.nextCursor;
    if (cursor === undefined || cursor === null || more || userId === null) {
      return;
    }
    setMore(true);
    try {
      const next = await api.get<TimelineResponse>(
        `/api/profiles/${encodeURIComponent(userId)}/timeline?cursor=${encodeURIComponent(cursor)}`,
      );
      setData((current) =>
        current === null || current.timeline === null
          ? current
          : {
              ...current,
              timeline: {
                ...current.timeline,
                events: [...current.timeline.events, ...next.events],
                nextCursor: next.nextCursor,
              },
            },
      );
    } catch (cause) {
      toast.show(t(errorMessageKey(cause)), 'error');
    } finally {
      setMore(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.show(t('common.copied'));
    } catch {
      // 클립보드 권한이 없거나 비보안 컨텍스트
      toast.show(t('error.generic'), 'error');
    }
  };

  // `@`로 시작하지 않으면 프로필 주소가 아니다. 오타이거나 없어진 앱 경로다.
  if (userId === null) {
    return <Navigate to="/" replace />;
  }

  if (notFound) {
    return (
      <Layout narrow>
        <div className={styles.empty}>
          <p className={styles.emptyText}>{t('profile.notFound')}</p>
          <p style={{ color: 'var(--muted)' }}>{t('profile.notFoundBody')}</p>
        </div>
      </Layout>
    );
  }

  if (error !== null) {
    return (
      <Layout narrow>
        <Notice tone="error">{error}</Notice>
      </Layout>
    );
  }

  if (data === null) {
    return (
      <Layout>
        <div className={styles.centered}>
          <Spinner label={t('common.loading')} />
        </div>
      </Layout>
    );
  }

  const { profile, picks, timeline, relation, connectionCount } = data;
  const visible = timeline !== null || picks !== null;

  return (
    <Layout>
      {/*
        내 프로필과 **같은 머리**다 — 사진 옆에 이름·주소·인연 수, 그 아래
        한마디, 맨 아래 행동 버튼. 남의 것을 보다가 내 것으로 왔을 때 구조가
        달라지면 같은 서비스로 안 읽힌다.
      */}
      <div className={styles.profileCard}>
        <div className={styles.profileTop}>
          {/* 내 프로필과 **같은 것**을 쓴다 — 따로 두면 언젠가 갈라진다 */}
          <Avatar name={profile.userName} imageUrl={profile.imageUrl} size={64} />

          <div className={styles.profileText}>
            <span className={styles.profileNameRow}>
              <h1 className={styles.profileName}>
                {profile.userName}
                {/* 잠긴 계정임을 이름 옆에서 바로 알린다 — 왜 안 보이는지의 답이다 */}
                {profile.isPrivate ? (
                  <span className={styles.lock}>
                    <LockIcon size={16} />
                  </span>
                ) : null}
              </h1>
              <p className={styles.profileHandle}>{profileHandle(profile.handle)}</p>
            </span>

            {/*
              인연 수는 **누를 수 있어야** 한다. 숫자만 있고 누를 데가 없으면
              그 자리가 장식이 된다 — 이 숫자를 보는 이유가 "누가 있는지"라서다.
              잠긴 계정이면 서버가 목록을 안 준다.

              내 프로필과 **같은 자리에 같은 클래스**로 둔다. 예전에는 여기만
              `<p>`라 세 번째 줄의 높이가 달랐고, 두 화면을 오가면 사진 아래가
              들썩였다.
            */}
            <button
              type="button"
              className={styles.connectionCount}
              onClick={() => navigate(`/@${profile.handle}/connections`)}
            >
              {t('connection.count', { count: connectionCount })}
            </button>
          </div>
        </div>

        {profile.bio === undefined || profile.bio === '' ? null : (
          <p className={styles.bio}>{profile.bio}</p>
        )}

        {/* 인연을 맺을지는 이름·주소·한마디를 다 읽고 정하는 일이다 */}
        <div className={styles.profileActions}>
          {relation === 'self' ? (
            <Button variant="outline" onClick={() => void copyLink()}>
              {t('profile.shareCopy')}
            </Button>
          ) : (
            <ConnectButton handle={profile.handle} relation={relation} onChanged={reload} />
          )}
        </div>
      </div>

      {/*
        못 보는 사람에게는 **여기서 끝난다.** 타임라인도 픽도 내려오지 않는다
        (서버가 아예 읽지 않는다). 무엇이 있는지 흘리지 않고 신청하라고만 한다.
      */}
      {!visible ? (
        <div className={styles.empty}>
          <p className={styles.emptyText}>{t('connection.lockedHint')}</p>
        </div>
      ) : null}

      {/*
        프로필은 **두 갈래**다 — 인생(타임라인)과 취향(픽). 내 프로필과 같은
        탭 줄을 쓴다(`ProfileTabs`) — 남의 것을 보다가 내 것으로 왔을 때
        구조가 달라지면 같은 서비스로 안 읽힌다.
      */}
      {visible ? <ProfileTabs tab={tab} onTab={setTab} /> : null}

      {visible && tab === 'timeline' ? (
        timeline === null || timeline.events.length === 0 ? (
          <EmptyState label={t('profile.emptyOther')} />
        ) : (
          <TimelineWithFilter
            topics={timeline.topics}
            events={timeline.events}
            {...(timeline.nextCursor === null
              ? {}
              : { onLoadMore: () => void loadMore(), loadingMore: more })}
          />
        )
      ) : null}

      {visible && tab === 'picks' ? (
        picks === null || picks.length === 0 ? (
          <EmptyState label={t('profile.emptyOther')} />
        ) : (
          <PickGrid>
            {picks.map((pick) => {
              const name =
                catalog === null ? pick.categoryId : categoryName(catalog, pick.categoryId);
              return (
                <PickGridCell
                  key={pick.categoryId}
                  item={{
                    categoryId: pick.categoryId,
                    categoryName: name,
                    itemName: pick.itemName,
                    ...(pick.itemImageUrl === undefined ? {} : { imageUrl: pick.itemImageUrl }),
                  }}
                  label={`${name} — ${pick.itemName}`}
                  onClick={() =>
                    setDetail({
                      categoryName: name,
                      itemName: pick.itemName,
                      ...(pick.itemDescription === undefined
                        ? {}
                        : { itemDescription: pick.itemDescription }),
                      ...(pick.itemImageUrl === undefined
                        ? {}
                        : { itemImageUrl: pick.itemImageUrl }),
                    })
                  }
                />
              );
            })}
          </PickGrid>
        )
      ) : null}

      <PickDetail pick={detail} onClose={() => setDetail(null)} />
    </Layout>
  );
}
